import { ref, watch } from 'vue'
import { useCheckoutForm } from './useCheckoutForm'
import { useCheckoutFormStore } from './useCheckoutFormStore'
import { useInstitutionStore } from '@entities/institution'
import { useUserProfileStore } from '@entities/user'

/**
 * Composable для управления формой в корзине
 */
export function useBasketForm() {
  const checkoutFormStore = useCheckoutFormStore()
  const institutionStore = useInstitutionStore()
  const userProfileStore = useUserProfileStore()
  
  // Состояния
  const isShowComment = ref(false)
  
  // Используем существующий composable для валидации
  const {
    v$,
    loader,
    onValidateS,
  } = useCheckoutForm({
    textTitleMessage: 'Новый заказ',
  })
  
  /**
   * Обрабатывает обновление метода получения заказа
   */
  function handleReceiptUpdate(updatedData: any): void {
    checkoutFormStore.OrderForm.method_receipt = updatedData.method_receipt
    
    switch (updatedData.method_receipt) {
      case 'delivery': {
        checkoutFormStore.setForm({
          delivery_address: updatedData.delivery_address || userProfileStore.getUser?.delivery_address,
          table_id: 0,
          address_institution: institutionStore.selectedBranch?.address,
        })
        break
      }
      case 'takeaway': {
        checkoutFormStore.setForm({
          delivery_address: '',
          table_id: 0,
          address_institution: updatedData.address_institution || institutionStore.selectedBranch?.address,
        })
        break
      }
      case 'dine_in': {
        checkoutFormStore.setForm({
          delivery_address: '',
          table_id: updatedData.table_id,
          address_institution: updatedData.address_institution || institutionStore.selectedBranch?.address,
        })
        break
      }
    }
  }
  
  /**
   * Переключает видимость блока комментария
   */
  function toggleCommentVisibility(): void {
    isShowComment.value = !isShowComment.value
  }
  
  /**
   * Получает данные пользователя для компонента MethodReceipt
   */
  function getUserDataForMethodReceipt() {
    return {
      method_receipt: checkoutFormStore.getForm.method_receipt,
      delivery_address: userProfileStore.selectedDeliveryAddress,
      table_id: checkoutFormStore.getForm.table_id,
      address_institution: checkoutFormStore.getForm.address_institution,
    }
  }
  
  /**
   * Получает ошибку адреса доставки
   */
  function getDeliveryAddressError() {
    return v$.value?.delivery_address
  }
  
  /**
   * Проверяет валидность адреса доставки
   */
  function isDeliveryAddressInvalid(): boolean {
    const addressError = getDeliveryAddressError()
    return addressError ? addressError.$invalid : false
  }
  
  /**
   * Получает текст ошибки адреса доставки
   */
  function getDeliveryAddressErrorText(): string {
    const addressError = getDeliveryAddressError()
    if (addressError && addressError.$invalid) {
      return addressError.$errors[0]?.$message || 'Укажите адрес доставки'
    }
    return ''
  }
  
  // Наблюдатель за изменениями формы для валидации
  watch(
    () => ({ ...checkoutFormStore.OrderForm }),
    () => {
      onValidateS()
    },
    { deep: true },
  )
  
  // Наблюдатель за номером телефона для сброса валидации
  watch(
    () => userProfileStore.getUser?.default_phone_number,
    () => {
      v$.value.$reset()
      v$.value.$validate()
    },
  )
  
  return {
    // State
    isShowComment,
    loader,
    v$,
    
    // Actions
    handleReceiptUpdate,
    toggleCommentVisibility,
    getUserDataForMethodReceipt,
    getDeliveryAddressError,
    isDeliveryAddressInvalid,
    getDeliveryAddressErrorText,
    onValidateS,
  }
}
